import type {
  LoyaltyPassProgramConfig,
  LoyaltyPassRewardConfig,
  LoyaltyPassRewardType,
  LoyaltyPassRule,
} from '../../types/loyaltyPass';
import {
  LOYALTY_PASS_DEFAULT_CARD_COLOR,
  LOYALTY_PASS_DEFAULT_VISITS,
  LOYALTY_PASS_MAX_VISITS,
  LOYALTY_PASS_MIN_VISITS,
} from '../../types/loyaltyPass';
import { normalizeLoyaltyPassRules } from './rulesConfig';

export type LoyaltyPassProgramValidationError =
  | 'loyaltyPass.program.errors.VISITS_OUT_OF_RANGE'
  | 'loyaltyPass.program.errors.REWARD_LABEL_REQUIRED'
  | 'loyaltyPass.program.errors.REWARD_VALUE_REQUIRED'
  | 'loyaltyPass.program.errors.REWARD_VALUE_INVALID'
  | 'loyaltyPass.program.errors.CARD_COLOR_INVALID';

export function defaultLoyaltyPassProgram(): LoyaltyPassProgramConfig {
  return {
    enabled: true,
    visitsRequired: LOYALTY_PASS_DEFAULT_VISITS,
    reward: {
      type: 'item',
      label: '',
      currency: 'XAF',
    },
    cardColor: LOYALTY_PASS_DEFAULT_CARD_COLOR,
  };
}

export function clampLoyaltyPassVisits(n: number): number {
  if (!Number.isFinite(n)) return LOYALTY_PASS_DEFAULT_VISITS;
  return Math.min(
    LOYALTY_PASS_MAX_VISITS,
    Math.max(LOYALTY_PASS_MIN_VISITS, Math.floor(n))
  );
}

function isHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value.trim());
}

export function normalizeLoyaltyPassReward(
  reward: Partial<LoyaltyPassRewardConfig> | undefined
): LoyaltyPassRewardConfig {
  const type: LoyaltyPassRewardType =
    reward?.type === 'percent' || reward?.type === 'fixed' || reward?.type === 'item'
      ? reward.type
      : 'item';
  const label = typeof reward?.label === 'string' ? reward.label.trim() : '';
  const value =
    typeof reward?.value === 'number' && Number.isFinite(reward.value)
      ? reward.value
      : undefined;
  return {
    type,
    label,
    currency: 'XAF',
    ...(value !== undefined ? { value } : {}),
  };
}

/**
 * Normalise une config programme (bornes + trim).
 * Ne valide pas le caractère « completable » — voir `validateLoyaltyPassProgram`.
 */
export function normalizeLoyaltyPassProgram(
  input: Partial<LoyaltyPassProgramConfig> | null | undefined
): LoyaltyPassProgramConfig {
  const base = defaultLoyaltyPassProgram();
  if (!input) return base;
  const cardColorRaw = input.cardColor?.trim();
  const rules = normalizeLoyaltyPassRules(input.rules);
  return {
    enabled: input.enabled !== false,
    visitsRequired: clampLoyaltyPassVisits(
      typeof input.visitsRequired === 'number'
        ? input.visitsRequired
        : base.visitsRequired
    ),
    reward: normalizeLoyaltyPassReward(input.reward),
    cardColor:
      cardColorRaw && isHexColor(cardColorRaw)
        ? cardColorRaw.toUpperCase()
        : LOYALTY_PASS_DEFAULT_CARD_COLOR,
    ...(rules.length > 0 ? { rules } : {}),
    ...(input.updatedAt ? { updatedAt: input.updatedAt } : {}),
  };
}

export function validateLoyaltyPassProgram(
  program: LoyaltyPassProgramConfig
): LoyaltyPassProgramValidationError | null {
  const visits = program.visitsRequired;
  if (
    !Number.isFinite(visits) ||
    visits < LOYALTY_PASS_MIN_VISITS ||
    visits > LOYALTY_PASS_MAX_VISITS
  ) {
    return 'loyaltyPass.program.errors.VISITS_OUT_OF_RANGE';
  }
  if (!program.reward.label.trim()) {
    return 'loyaltyPass.program.errors.REWARD_LABEL_REQUIRED';
  }
  if (program.reward.type === 'percent' || program.reward.type === 'fixed') {
    const v = program.reward.value;
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      return 'loyaltyPass.program.errors.REWARD_VALUE_REQUIRED';
    }
    if (program.reward.type === 'percent' && (v < 1 || v > 100)) {
      return 'loyaltyPass.program.errors.REWARD_VALUE_INVALID';
    }
    if (program.reward.type === 'fixed' && v < 1) {
      return 'loyaltyPass.program.errors.REWARD_VALUE_INVALID';
    }
  }
  if (program.cardColor && !isHexColor(program.cardColor)) {
    return 'loyaltyPass.program.errors.CARD_COLOR_INVALID';
  }
  return null;
}

export function isLoyaltyPassProgramConfigured(
  program: LoyaltyPassProgramConfig | null | undefined
): boolean {
  if (!program) return false;
  return validateLoyaltyPassProgram(normalizeLoyaltyPassProgram(program)) === null;
}

/**
 * Assemble le programme à persister : règles + miroir BS (visits/reward)
 * dérivé de la 1ʳᵉ règle active (wallet / champs plats).
 */
export function buildLoyaltyPassProgramWithRules(input: {
  base: LoyaltyPassProgramConfig;
  rules: LoyaltyPassRule[];
  enabled?: boolean;
  cardColor?: string;
}): LoyaltyPassProgramConfig {
  const rules = normalizeLoyaltyPassRules(input.rules);
  const primary =
    rules.find((r) => r.enabled) ?? (rules.length > 0 ? rules[0] : undefined);

  const reward: LoyaltyPassRewardConfig = primary
    ? {
        type: primary.reward.type,
        value: primary.reward.value,
        label: primary.reward.label,
        currency: 'XAF',
      }
    : input.base.reward;

  const visitsRequired = primary
    ? clampLoyaltyPassVisits(
        Math.max(LOYALTY_PASS_MIN_VISITS, primary.visitsRequired)
      )
    : input.base.visitsRequired;

  return normalizeLoyaltyPassProgram({
    ...input.base,
    enabled: input.enabled ?? input.base.enabled,
    cardColor: input.cardColor ?? input.base.cardColor,
    visitsRequired,
    reward,
    rules,
  });
}

/**
 * Payload merchant BonSpot (champs loyalty* + rules optionnelles) à partir de la config NG.
 * `loyaltyRules` absentes / [] → BS reste en mode programme simple V1.
 */
export function toBonspotMerchantLoyaltyFields(program: LoyaltyPassProgramConfig): {
  loyaltyEnabled: boolean;
  loyaltyEarnMode: 'stamps';
  loyaltyVisitsRequired: number;
  loyaltyRewardLabel: string;
  loyaltyReward: {
    type: LoyaltyPassRewardType;
    value?: number;
    currency: 'XAF';
    label: string;
  };
  loyaltyCardColor: string;
  loyaltyRules?: LoyaltyPassProgramConfig['rules'];
} {
  const normalized = normalizeLoyaltyPassProgram(program);
  return {
    loyaltyEnabled: normalized.enabled,
    loyaltyEarnMode: 'stamps',
    loyaltyVisitsRequired: normalized.visitsRequired,
    loyaltyRewardLabel: normalized.reward.label,
    loyaltyReward: {
      type: normalized.reward.type,
      ...(normalized.reward.value !== undefined
        ? { value: normalized.reward.value }
        : {}),
      currency: 'XAF',
      label: normalized.reward.label,
    },
    loyaltyCardColor: normalized.cardColor || LOYALTY_PASS_DEFAULT_CARD_COLOR,
    ...(normalized.rules && normalized.rules.length > 0
      ? { loyaltyRules: normalized.rules }
      : {}),
  };
}
